import { Reducer } from "redux"
import { sortByAppearanceInDomTree } from "../../core/helpers/webcomponent-helpers"
import { add, remove } from "../../core/helpers/immutability-helpers"

export interface AccordionState {
  accordionItemEls: HTMLSdxAccordionItemElement[]
  openAccordionItemEls: HTMLSdxAccordionItemElement[]
  arrowPosition: HTMLSdxAccordionElement["arrowPosition"]
  theme: "borderless" | ""
  keepOpen: boolean
  isUserInteractionInProgress: boolean
}

export type AccordionActions =
  | {
      type: "ADD_ACCORDION_ITEM_EL"
      accordionItemEl: HTMLSdxAccordionItemElement
    }
  | {
      type: "REMOVE_ACCORDION_ITEM_EL"
      accordionItemEl: HTMLSdxAccordionItemElement
    }
  | { type: "UPDATE_REAPPENDING" }
  | {
      type: "OPEN_ACCORDION_ITEM_EL"
      accordionItemEl: HTMLSdxAccordionItemElement
    }
  | { type: "OPEN_ALL_ACCORDION_ITEM_ELS" }
  | {
      type: "CLOSE_ACCORDION_ITEM_EL"
      accordionItemEl: HTMLSdxAccordionItemElement
    }
  | { type: "CLOSE_ALL_ACCORDION_ITEM_ELS" }
  | {
      type: "TOGGLE_ACCORDION_ITEM_EL"
      accordionItemEl: HTMLSdxAccordionItemElement
    }

function open(
  state: AccordionState,
  accordionItemEl: HTMLSdxAccordionItemElement,
): AccordionState {
  // Do nothing if it's already open
  if (state.openAccordionItemEls.includes(accordionItemEl)) {
    return state
  }

  return {
    ...state,
    openAccordionItemEls: state.keepOpen
      ? [...state.openAccordionItemEls, accordionItemEl]
      : [accordionItemEl],
  }
}

function close(
  state: AccordionState,
  accordionItemEl: HTMLSdxAccordionItemElement,
): AccordionState {
  // Do nothing if it's already closed
  if (!state.openAccordionItemEls.includes(accordionItemEl)) {
    return state
  }

  return {
    ...state,
    openAccordionItemEls: state.openAccordionItemEls.filter(
      (el) => el !== accordionItemEl,
    ),
  }
}

export const accordionReducer: Reducer<AccordionState, AccordionActions> = (
  state = {} as AccordionState,
  action,
): AccordionState => {
  switch (action.type) {
    case "ADD_ACCORDION_ITEM_EL":
      return {
        ...state,
        accordionItemEls: add(
          state.accordionItemEls,
          action.accordionItemEl,
        ).sort(sortByAppearanceInDomTree),
      }

    case "REMOVE_ACCORDION_ITEM_EL":
      return {
        ...state,
        accordionItemEls: remove(
          state.accordionItemEls,
          action.accordionItemEl,
        ).sort(sortByAppearanceInDomTree),
      }

    case "OPEN_ACCORDION_ITEM_EL":
      return open(state, action.accordionItemEl)

    case "OPEN_ALL_ACCORDION_ITEM_ELS":
      return { ...state, openAccordionItemEls: state.accordionItemEls }

    case "CLOSE_ACCORDION_ITEM_EL":
      return close(state, action.accordionItemEl)

    case "CLOSE_ALL_ACCORDION_ITEM_ELS":
      return { ...state, openAccordionItemEls: [] }

    case "TOGGLE_ACCORDION_ITEM_EL":
      const isOpen = state.openAccordionItemEls.includes(action.accordionItemEl)

      return isOpen
        ? close(state, action.accordionItemEl)
        : open(state, action.accordionItemEl)

    default:
      return state
  }
}

export function getInitialState(): AccordionState {
  return {
    accordionItemEls: [],
    openAccordionItemEls: [],
    arrowPosition: "right",
    theme: "",
    keepOpen: false,
    isUserInteractionInProgress: false,
  }
}
